import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { format } from "date-fns";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";

import { getDailyAnalytics } from "../../redux/slices/reportSlice";
import {
  axisProps,
  gridProps,
  compactNumber,
  ChartTooltip,
  ChartLegend,
  ChartEmpty,
} from "../common/chart";
import theme from "../../theme";

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;

  input {
    height: 40px;
    border: 1px solid ${theme.color.borderStrong};
    border-radius: ${theme.radius.sm};
    padding: 0 14px;
    font-family: inherit;
    font-size: ${theme.font.body};
    color: ${theme.color.text};
    background: ${theme.color.surface};

    &:focus {
      outline: none;
      border-color: ${theme.color.brand};
      box-shadow: 0 0 0 3px ${theme.color.brandSoft};
    }
  }
`;

const formatHour = (hour) => {
  const period = hour >= 12 ? "PM" : "AM";
  const twelveHour = hour % 12 || 12;
  return `${twelveHour} ${period}`;
};

const Hourlychart = () => {
  const dispatch = useDispatch();
  const { dailyAnalytics } = useSelector((state) => state.report);

  const currentDay = format(new Date(), "yyyy-MM-dd");
  const [singleDay, setSingleDay] = useState(currentDay);

  useEffect(() => {
    dispatch(getDailyAnalytics({ singleDay }));
  }, [dispatch, singleDay]);

  const data =
    dailyAnalytics?.data?.map((item) => ({
      name: formatHour(item?.hour),
      Conversions: Number(item?.conversioncount) || 0,
    })) || [];

  const hasData = data.some((point) => point.Conversions > 0);

  return (
    <div>
      <Toolbar>
        <ChartLegend keys={["Conversions"]} />
        <input
          type="date"
          value={singleDay}
          onChange={(event) => setSingleDay(event?.target?.value)}
          aria-label="Select day"
        />
      </Toolbar>

      {hasData ? (
        <ResponsiveContainer width="100%" height={320}>
          <BarChart
            data={data}
            margin={{ top: 8, right: 8, left: -12, bottom: 0 }}
          >
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="name" {...axisProps} interval="preserveStartEnd" />
            <YAxis tickFormatter={compactNumber} {...axisProps} />
            <Tooltip
              cursor={{ fill: theme.color.surfaceAlt }}
              content={<ChartTooltip />}
            />
            <Bar
              dataKey="Conversions"
              fill={theme.chart[0]}
              radius={[6, 6, 0, 0]}
              maxBarSize={28}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <ChartEmpty>No conversions recorded on this day.</ChartEmpty>
      )}
    </div>
  );
};

export default Hourlychart;
